import { useCallback, useRef } from "react";

/**
 * Debounce and cancellation for the calendar's updates (observation 13).
 *
 * - `schedule` runs the work after a short delay. A newer call replaces the pending work,
 *   so a quick second click is the one that counts.
 * - `startRequest` aborts the request still in flight and returns a signal for the new one.
 *   A newer request always wins; it is never dropped because an older one is still busy.
 */
export function useRequestManager() {
  // Debounce: the pending work, not just a placeholder timer
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTimer = useCallback(() => {
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
  }, []);
  const schedule = useCallback(
    (work: () => void, milliseconds: number) => {
      clearTimer();
      transitionTimerRef.current = setTimeout(() => {
        transitionTimerRef.current = null;
        work();
      }, milliseconds);
    },
    [clearTimer]
  );

  // Abort controller management
  const abortControllerRef = useRef<AbortController | null>(null);
  const cancelPreviousRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);
  const startRequest = useCallback((): AbortController => {
    cancelPreviousRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller;
  }, [cancelPreviousRequest]);
  const finishRequest = useCallback((controller: AbortController) => {
    if (abortControllerRef.current === controller) {
      abortControllerRef.current = null;
    }
  }, []);
  const isRequestInFlight = useCallback(() => abortControllerRef.current !== null, []);
  const isScheduled = useCallback(() => transitionTimerRef.current !== null, []);

  return {
    clearTimer,
    schedule,
    isScheduled,
    cancelPreviousRequest,
    startRequest,
    finishRequest,
    isRequestInFlight
  };
}
